import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import { Button, Box, Paper, TablePagination } from '@material-ui/core';
import Searchbar from '../../components/Searchbar';
import { Spinner } from '../../components/UI';
import { EmptyIcon } from '../../assets/svg';
import FilterModal, {
    FilteredState,
    FILTER_INPUT_TYPE,
} from '../../components/Modals/FilterModal';
import moment from 'moment';
import { fetchReferralPoolList } from '../../store/actions/agencyActionCreator';
import ReferralReportItem from './ReferralReportItem';

const defaultDateRange = {
    poolDate: {
        startDate: moment().subtract(6, 'days').startOf('day').valueOf(),
        endDate: moment().endOf('day').valueOf(),
    },
    startDate: moment().subtract(6, 'days').format('YYYY-MM-DD'),
    endDate: moment().format('YYYY-MM-DD'),
};

const ReferralReport = ({ dispatchFetchReferralPoolList }) => {
    const [loading, setLoading] = useState(false);
    const [rows, setRows] = useState([]);
    const [hasMore, setHasMore] = useState(false);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [searchVal, setSearchVal] = useState('');
    const [filteredValues, setFilteredValues] = useState({
        ...defaultDateRange,
    });
    const [payload, setPayload] = useState({
        ...defaultDateRange,
        status: null,
        searchText: null,
        pageNo: 1,
        pageSize: 10,
    });

    const fetchList = (p) => {
        setLoading(true);
        const { poolDate, ...apiPayload } = p;
        dispatchFetchReferralPoolList(
            apiPayload,
            (resp) => {
                setLoading(false);
                setRows(resp?.response?.records ?? []);
                setHasMore(resp?.response?.hasMore ?? false);
            },
            () => setLoading(false),
        );
    };

    useEffect(() => {
        fetchList(payload);
    }, [payload]);

    const filterOptions = (values) => [
        {
            type: FILTER_INPUT_TYPE.DATERANGE,
            label: 'Upload Date',
            name: 'poolDate',
            value: values?.poolDate ?? undefined,
            onChange: (e) => {
                setFilteredValues({
                    ...values,
                    poolDate: e,
                    startDate: moment(e.startDate).format('YYYY-MM-DD'),
                    endDate: moment(e.endDate).format('YYYY-MM-DD'),
                });
            },
            render: () => {
                if (
                    moment(values?.poolDate?.startDate)?.valueOf() ===
                        moment()
                            .subtract(6, 'days')
                            .startOf('day')
                            ?.valueOf() &&
                    moment(values?.poolDate?.endDate)?.valueOf() ===
                        moment().endOf('day')?.valueOf()
                )
                    return `Date: Last 7 Days`;
                return `Date: ${values.startDate} - ${values.endDate}`;
            },
        },
        {
            type: FILTER_INPUT_TYPE.SELECT,
            label: 'Status',
            name: 'status',
            value: values?.statusOption ?? null,
            options: ['PENDING', 'PROCESSING', 'PROCESSED', 'FAILED'].map(
                (s) => ({ label: s, value: s }),
            ),
            keysToDelete: ['status', 'statusOption'],
            onChange: (e) => {
                setFilteredValues({
                    ...values,
                    status: e?.target?.value?.value ?? null,
                    statusOption: e?.target?.value ?? null,
                });
            },
        },
    ];

    const onFilterApplied = () => {
        setPage(0);
        setPayload((prev) => ({
            ...prev,
            startDate: filteredValues.startDate,
            endDate: filteredValues.endDate,
            status: filteredValues.statusOption?.value ?? null,
            pageNo: 1,
            pageSize: rowsPerPage,
        }));
    };

    return (
        <Spinner loading={loading}>
            <Paper style={{ padding: '12px 0' }} elevation={0}>
                <Box
                    className="horiz-end"
                    style={{ margin: '24px 16px 16px 24px' }}
                >
                    <Box
                        style={{
                            width: '400px',
                            display: 'flex',
                            flexDirection: 'row',
                            justifyContent: 'space-between',
                        }}
                    >
                        <Searchbar
                            placeHolderText="Search by Contact No."
                            backendSearchbar={true}
                            onBackendSearch={(searchText) => {
                                setPage(0);
                                setSearchVal(searchText);
                            }}
                            value={searchVal}
                            keys={['referentContactNumber']}
                            onFilter={() => {}}
                            onChange={(value) => setSearchVal(value)}
                        />
                        <Button
                            style={{ marginLeft: 8 }}
                            variant="outlined"
                            color="primary"
                            onClick={() => {
                                const isReset =
                                    !!payload?.searchText &&
                                    payload?.searchText === searchVal;
                                setPage(0);
                                setPayload((prev) => ({
                                    ...prev,
                                    pageNo: 1,
                                    searchText: isReset
                                        ? null
                                        : searchVal || null,
                                }));
                                if (isReset) setSearchVal('');
                            }}
                            disabled={!searchVal}
                        >
                            {!!payload?.searchText &&
                            payload?.searchText === searchVal
                                ? 'Reset'
                                : 'Search'}
                        </Button>
                        <FilterModal
                            filterOptions={filterOptions(filteredValues)}
                            filteredValues={filteredValues}
                            onFilterApplied={onFilterApplied}
                        />
                    </Box>
                </Box>

                <FilteredState
                    filterOptions={filterOptions(filteredValues)}
                    filteredValues={filteredValues}
                    onDelete={(itemKey, item) => {
                        let next = { ...filteredValues };
                        if (item?.keysToDelete?.length > 0) {
                            item.keysToDelete.forEach((k) => delete next[k]);
                        } else {
                            delete next[itemKey];
                        }
                        if (itemKey === 'poolDate') {
                            delete next.startDate;
                            delete next.endDate;
                        }
                        setFilteredValues(next);
                        let nextPayload = { ...payload };
                        delete nextPayload[itemKey];
                        if (itemKey === 'poolDate') {
                            delete nextPayload.startDate;
                            delete nextPayload.endDate;
                        }
                        if (itemKey === 'status') delete nextPayload.status;
                        setPayload({ ...nextPayload, ...next, pageNo: 1 });
                        setPage(0);
                    }}
                />

                {rows.length === 0 ? (
                    <Box
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                        }}
                    >
                        <EmptyIcon />
                    </Box>
                ) : (
                    <Box style={{ margin: '0 24px 0 24px' }}>
                        <ReferralReportItem data={rows} />
                        <TablePagination
                            rowsPerPageOptions={[10, 50, 100]}
                            component="div"
                            count={
                                hasMore
                                    ? (page + 2) * rowsPerPage
                                    : page * rowsPerPage + rows.length
                            }
                            rowsPerPage={rowsPerPage}
                            page={page}
                            onChangePage={(_, newPage) => {
                                setPage(newPage);
                                setPayload((prev) => ({
                                    ...prev,
                                    pageNo: newPage + 1,
                                }));
                            }}
                            onChangeRowsPerPage={(e) => {
                                const size = +e.target.value;
                                setRowsPerPage(size);
                                setPage(0);
                                setPayload((prev) => ({
                                    ...prev,
                                    pageNo: 1,
                                    pageSize: size,
                                }));
                            }}
                            labelDisplayedRows={({ from, to }) =>
                                `${from}-${to}`
                            }
                        />
                    </Box>
                )}
            </Paper>
        </Spinner>
    );
};

const mapDispatchToProps = (dispatch) => ({
    dispatchFetchReferralPoolList: (data, onSuccess, onError) =>
        dispatch(fetchReferralPoolList(data, onSuccess, onError)),
});

export default connect(null, mapDispatchToProps)(ReferralReport);
